import { Grid, GridSize, TextField } from "@material-ui/core";
import { ReactComponent as CenterDotIcon } from "assets/CenterDotIcon.svg";
import useComments from "backend/hooks/comments/useComments";
import useAttachments from "backend/hooks/files/useAttachments";
import { ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import RichTextViewer from "qpr-base/ui/components/RichTextViewer";
import UploadedFilesList from "qpr-base/ui/components/UploadedFilesList";
import React from "react";
import { useAppContext } from "../../qpr-base/context/AppCtxProvider";
import { makeThemedStyles } from "../../qpr-base/viewcomponents/utils";

interface UpdateProps {
  value: string;
  id?: string;
  name?: string;
}
interface Props {
  element: ICardElementWithChildren;
  isDisabled: boolean;
  handleField: (val: UpdateProps) => any;
}
const useStyles = (rtl: boolean) =>
  makeThemedStyles("", (theme) => ({
    cellContent: { display: "flex", flexWrap: "nowrap" },
    heading: {
      background: "none",
      textAlign: "start",
      fontFamily: "Arial",
      fontSize: "12px",
      display: "flex",
      flewWrap: "nowrap",
      fontWeight: 400,
      lineHeight: "1.25em",
      letterSpacing: "0em",
      color: "#374151",
      margin: 0,
      marginTop: "8px",
      marginInlineStart: "22px",
    },
    heading2: { float: "right" },
    tableField: {
      height: "4.342em",
      display: "flex",
      width: "160px",
      flexDirection: "row",
      alignItems: "center",
      padding: "0em 1.828em",
      background: "rgba(250, 250, 250, 1)",
      border: "0.114em solid rgba(209, 213, 219, 1)",
      boxShadow: "0em 0.114em 0.228em rgba(0, 0, 0, 0.05)",
      borderRadius: "1.257em",
      margin: "0.4em 1em 0.914em 2.2em",
    },
    centerdot: {
      marginTop: "-2px",
    },
    unitSymbol: {
      fontSize: "14px",
      marginTop: "11px",
    },
    typeName: {
      fontSize: "14px",
      margin: "12px 0 0 60px",
    },
    attachments: {
      margin: "5px 0 14px 20px",
    },
  }));
const SubElementContents = (props: Props) => {
  const ctx = useAppContext();
  const classes = useStyles(ctx.rtl)();
  const { handleField, isDisabled, element } = props;
  const id = element.id;
  const period = element.periodname || "";

  const { data: comments, isLoading: isLoadingComments } = useComments({
    id,
    period,
  });
  const { data: attachments, isLoading: isLoadingAttachments } = useAttachments(
    { elementId: id, period }
  );

  const handleChange = (e: {
    target: {
      value: string;
      name: string;
    };
  }) => {
    const { value, name } = e.target;
    if (new RegExp("^-?[0-9]*$").test(value)) {
      handleField({ value: value, name: name, id: name });
    }
  };

  const getSubElementFields = () => {
    const visibleSeries =
      element.typename === "Milestone"
        ? [
            {
              series: "END_DATE",
              attribute: "prettyvalue",
            },
            {
              series: "COMPLETION_PERCENTAGE",
              attribute: "valuewithoutsymbol",
            },
          ]
        : [
            { series: "ACT", attribute: "valuewithoutsymbol" },
            { series: "TAR", attribute: "valuewithoutsymbol" },
          ];
    const spacePerCell: number = Math.floor(
      12 / Math.min(visibleSeries.length, 3)
    );

    const getSeriesValue = (series: string, attribute: string) => {
      return (element as any)[`${series}_${attribute}`];
    };

    return visibleSeries.map((ser: any) => (
      <Grid item xs={spacePerCell as GridSize}>
        <>
          <div className={classes.cellContent}>
            <div className={classes.heading}>
              {t(`configtypes.${ser.series}`)}
            </div>
          </div>
          <div className={classes.cellContent}>
            <div
              className={classes.tableField}
              style={{
                background: isDisabled ? "rgb(250, 250, 250)" : undefined,
              }}
            >
              <TextField
                disabled={isDisabled}
                name={id}
                type="text"
                fullWidth={false}
                value={getSeriesValue(ser.series, ser.attribute)}
                onChange={handleChange}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: "16px",
                    color: "rgba(76, 81, 87, 1)",
                  },
                }}
              />
            </div>
            <div className={classes.unitSymbol}>
              {t(getSeriesValue(ser.series, "unitname"))}
            </div>
          </div>
        </>
      </Grid>
    ));
  };

  if (isLoadingAttachments || isLoadingComments) return null;

  return (
    <>
      <Grid container spacing={0}>
        {getSubElementFields()}
      </Grid>

      {attachments?.length || comments?.length ? (
        <div className={classes.heading}>
          {t("kpis.Actions and Recommendations")}
          <div className={classes.centerdot}>
            <CenterDotIcon />
          </div>
          <div>{comments?.length ? comments[0].modifiedby : ""}</div>
        </div>
      ) : null}

      {comments?.length ? (
        <div>
          <RichTextViewer
            value={comments?.length ? comments?.[0].description : ""}
          />
        </div>
      ) : null}

      {attachments?.length ? (
        <>
          <div className={classes.attachments}>
            <UploadedFilesList values={attachments} readOnly />
          </div>
        </>
      ) : null}
    </>
  );
};
export default SubElementContents;
